import { Fragment, useState, useEffect, useCallback } from "react";
import { useSnackbar } from "notistack";
import makeStyles from "styles/muiStylesCompat/makeStyles";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import CheckCircle from "@mui/icons-material/CheckCircle";

import SpinnerCentered from "~/components/Loader/SpinnerCentered";
import ButtonSuccess from "~/components/Buttons/ButtonSuccess";
import { getDrivers } from "~/services/http/drivers";
import { httpGetInvitedUsers, httpInviteResend } from "services/http/companies";
import { validateCompany } from "services/companies";
import AlertDialog from "components/AlertDialog";

const useStyles = makeStyles((theme) => ({
  overrides: {
    MuiTableCell: {
      root: {
        fontWeight: "bold",
        backgroundColor: "lightblue",
        "&:last-child": { paddingRight: 5 },
      },
    },
  },
  content: { padding: 48, overflowX: "auto" },
  title: { paddingBottom: 32 },
  table: {
    minWidth: 650,
    borderWidth: 1,
    borderColor: theme.palette.table.border,
    borderStyle: "solid",
    fontSize: 16,
    fontWeight: "bold",
  },
  tableHeaderCell: {
    color: theme.palette.table.thead.text,
    fontSize: 16,
    fontWeight: 900,
    backgroundColor: theme.palette.table.thead.background,
  },
  logo: { width: 24, height: 24, marginLeft: 2 },
  notConfirmed: { color: "#B5B5B5" },
  confirmed: { color: "#87CC72" },
  buttonLink: { textDecoration: "none" },
}));

const DriversList = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();
  const classes = useStyles();
  const navigate = useNavigate();
  const { myCompany, myUser } = props;

  const [rows, setRows] = useState([]);
  const [invitedUsers, setInvitedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);

  const handleValidateCompany = useCallback(() => {
    if (validateCompany(myCompany) === false) {
      setAlertOpen(true);
    }
  }, [myCompany]);

  const handleGetDrivers = useCallback(() => {
    setLoading(true);
    const request = { params: { drivers: true, company: myCompany?.id } };

    getDrivers(request).then(
      (response) => {
        setRows(response);
        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      },
    );
  }, [myCompany?.id]);

  const handleGetUserInvites = useCallback(() => {
    setLoading(true);

    const request = {};

    httpGetInvitedUsers(request).then(
      (response) => {
        let _users = [];
        response.map((user) => {
          if (user?.status < 100 && !user?.completed) {
            _users.push(user);
          }
        });
        setInvitedUsers(_users);
        setLoading(false);
      },
      (_error) => {
        setLoading(false);
      },
    );
  }, []);

  useEffect(() => {
    handleValidateCompany();
    handleGetDrivers();
    handleGetUserInvites();
  }, [handleValidateCompany, handleGetDrivers, handleGetUserInvites]);

  const getResendErrorMessage = (error) => {
    const code = error?.data?.errorCode;
    const messages = {
      INVITE_ALREADY_ACCEPTED: t("Kutsu on jo hyväksytty."),
      EMAIL_SEND_FAILED: t(
        "Sähköpostin lähetys epäonnistui. Yritä myöhemmin uudelleen.",
      ),
      PERMISSION_DENIED: t("Sinulla ei ole oikeutta lähettää kutsua uudelleen."),
      INVITE_NOT_FOUND: t("Kutsua ei löydy."),
    };
    return (
      messages[code] ||
      error?.data?.detail ||
      t("Kutsun lähetys epäonnistui")
    );
  };

  const handleInviteResend = (id) => {
    httpInviteResend(id).then(
      (_response) => {
        enqueueSnackbar(t("Kutsu lähetetty"), {
          variant: "success",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 2000,
        });
        handleGetUserInvites();
      },
      (error) => {
        enqueueSnackbar(getResendErrorMessage(error), {
          variant: "error",
          anchorOrigin: { vertical: "bottom", horizontal: "center" },
          autoHideDuration: 4000,
        });
      },
    );
  };

  return (
    <Paper className={classes.content} elevation={0} square>
      <Grid
        container
        className={classes.title}
        spacing={3}
        direction="row"
        sx={{ justifyContent: "space-between", alignItems: "center" }}
      >
        <Grid size={12}>
          <Typography variant="h4">{t("Kuljettajat")}</Typography>
          {myUser?.status < 100 ? (
            <Typography>
              {t("Vain yrityksen ylläpitäjä pystyy lisäämään kuljettajia")}
            </Typography>
          ) : null}
        </Grid>

        {myUser?.status == 100 ? (
          <Grid size={12}>
            <Link className={classes.buttonLink} to={"/dashboard/drivers/new"}>
              <ButtonSuccess>{t("Lisää kuljettaja")}</ButtonSuccess>
            </Link>
          </Grid>
        ) : null}
      </Grid>

      {loading ? (
        <SpinnerCentered />
      ) : (
        <Fragment>
          <Box sx={{ mb: 4 }}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  {myUser?.status == 100 ? (
                    <TableCell className={classes.tableHeaderCell} align="left">
                      {t("Tarkastettu")}
                    </TableCell>
                  ) : null}
                  <TableCell className={classes.tableHeaderCell} align="left">
                    {t("Kuljettaja")}
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="left">
                    {t("Liitetty autoon")}
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={
                      row.id != null
                        ? `driver-${row.id}`
                        : `driver-${row.email}`
                    }
                  >
                    {myUser?.status == 100 ? (
                      <TableCell>
                        <CheckCircle
                          fontSize="default"
                          className={
                            row.verified
                              ? classes.confirmed
                              : classes.notConfirmed
                          }
                        />
                      </TableCell>
                    ) : null}
                    <TableCell component="th" scope="row">
                      {myUser?.status == 100 || myUser?.id == row.id ? (
                        <Link
                          className={classes.buttonLink}
                          to={"/dashboard/drivers/" + row.id + "/edit"}
                        >
                          <Typography color="primary" variant="subtitle2">
                            {row.first_name} {row.last_name}
                          </Typography>
                        </Link>
                      ) : (
                        <Typography variant="subtitle2">
                          {row.first_name} {row.last_name}
                        </Typography>
                      )}
                      <Typography>{row.email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2">
                        {row?.car?.license_number}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {myUser?.status == 100 || myUser?.id == row.id ? (
                        <Button
                          component={Link}
                          to={"/dashboard/drivers/" + row.id + "/edit"}
                        >
                          <Typography color="primary" variant="subtitle2">
                            {t("Avaa tiedot")}
                          </Typography>
                        </Button>
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          <Box sx={{ mb: 2 }}>
            <Typography variant="h4">{t("Kuljettajakutsut")}</Typography>
          </Box>

          <Box sx={{ mb: 4 }}>
            <Table className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell className={classes.tableHeaderCell} align="left">
                    {t("Sähköposti")}
                  </TableCell>
                  <TableCell className={classes.tableHeaderCell} align="right">
                    &nbsp;
                  </TableCell>
                </TableRow>
              </TableHead>

              {loading ? (
                <TableBody>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <SpinnerCentered />
                    </TableCell>
                  </TableRow>
                </TableBody>
              ) : (
                <TableBody>
                  {invitedUsers.map((row) => (
                    <TableRow
                      key={
                        row.id != null
                          ? `invite-${row.id}`
                          : `invite-${row.email}`
                      }
                    >
                      <TableCell>{row.email}</TableCell>
                      <TableCell align="right">
                        {myUser?.status == 100 ? (
                          <Button onClick={() => handleInviteResend(row.id)}>
                            <Typography color="primary" variant="subtitle2">
                              {t("Lähetä uudelleen")}
                            </Typography>
                          </Button>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              )}
            </Table>
          </Box>
        </Fragment>
      )}

      <AlertDialog
        titleText={t("Huom!")}
        contentText={t(
          "Sinun täytyy lisätä yrityksesi maksutiedot ja hinnasto, jotta voit lisätä kuljettajia ja autoja.",
        )}
        open={alertOpen}
        setOpen={setAlertOpen}
        showCancel={false}
        confirmText={t("OK")}
        handleConfirm={() => navigate("/dashboard/start")}
        isModal={true}
      />
    </Paper>
  );
};

export default DriversList;
